import { TicketsStatusReference } from '@/types/codegen-federation'

/**
 * Represents an availability reason from the watchableReasons or actionsToWatch array.
 * Used to determine why content is available (e.g., publicly available, guild access, etc.)
 */
type AvailabilityReason = {
  __typename?: string | null
  startDatetime?: string | null
}

/**
 * Content with watchable reasons that can be checked for streaming availability.
 * Supports both watchableReasons (preferred) and actionsToWatch (legacy) arrays.
 * For ContentSeries, pass the firstWatchable content instead.
 */
export type ContentWithWatchableReasons = {
  watchableReasons?: Array<AvailabilityReason | null> | null
  actionsToWatch?: Array<AvailabilityReason | null> | null
  watchableAvailabilityStatus?: string | null
}

/**
 * Gets the availability reasons array, preferring watchableReasons over actionsToWatch.
 */
const getAvailabilityReasons = (
  content?: ContentWithWatchableReasons | null,
): Array<AvailabilityReason | null> | null => {
  // Prefer watchableReasons if available
  if (content?.watchableReasons && content.watchableReasons.length > 0) {
    return content.watchableReasons
  }
  // Fall back to actionsToWatch
  return content?.actionsToWatch ?? null
}

/**
 * Checks if the availability reasons array contains a specific availability action type.
 */
const hasAvailabilityReason = (
  reasons: Array<AvailabilityReason | null> | null | undefined,
  typename: string,
): boolean => {
  return reasons?.some((reason) => reason?.__typename === typename) ?? false
}

/**
 * Checks if content is in free access (publicly available to everyone).
 * This checks for PubliclyAvailable in watchableReasons, which indicates
 * the content is free regardless of the user's guild membership status.
 *
 * For ContentSeries, pass the firstWatchable content.
 *
 * @param content - Content with watchableReasons array
 */
export const isInFreeAccess = (content?: ContentWithWatchableReasons | null): boolean => {
  const reasons = getAvailabilityReasons(content)
  // Check for PubliclyAvailable in availability reasons
  if (hasAvailabilityReason(reasons, 'PubliclyAvailable')) {
    return true
  }
  // Fallback: if watchableAvailabilityStatus is WATCHABLE and no guild access reason, it's publicly available
  if (
    content?.watchableAvailabilityStatus === 'WATCHABLE' &&
    !hasAvailabilityReason(reasons, 'GuildAvailabilityAction')
  ) {
    return true
  }
  return false
}

/**
 * Checks if content is in guild access (available to guild members NOW).
 * This checks for GuildAvailabilityAction in watchableReasons and verifies
 * the startDatetime is in the past (content is currently available).
 *
 * For ContentSeries, pass the firstWatchable content.
 *
 * @param content - Content with watchableReasons array
 */
export const isInGuildAccess = (content?: ContentWithWatchableReasons | null): boolean => {
  const reasons = getAvailabilityReasons(content)
  // Check for GuildAvailabilityAction with a valid startDatetime
  const guildReason = reasons?.find((reason) => reason?.__typename === 'GuildAvailabilityAction')

  if (guildReason) {
    // If there's a startDatetime, check if it's in the past
    if (guildReason.startDatetime) {
      const startDate = new Date(guildReason.startDatetime)
      return startDate <= new Date()
    }
    // No startDatetime means it's available now
    return true
  }

  // Fallback: if watchableAvailabilityStatus is ACTION_REQUIRED and no reasons to check
  if (content?.watchableAvailabilityStatus === 'ACTION_REQUIRED') {
    // Only return true if there are no availability reasons to check (data might be incomplete)
    if (!reasons || reasons.length === 0) {
      return true
    }
  }

  return false
}

/**
 * Checks if content has upcoming guild access (will be available to guild members in the future).
 * Returns true if there's a GuildAvailabilityAction with a startDatetime in the future.
 *
 * @param content - Content with watchableReasons array
 */
export const hasUpcomingGuildAccess = (content?: ContentWithWatchableReasons | null): boolean => {
  const reasons = getAvailabilityReasons(content)
  const guildReason = reasons?.find((reason) => reason?.__typename === 'GuildAvailabilityAction')

  if (guildReason?.startDatetime) {
    const startDate = new Date(guildReason.startDatetime)
    return startDate > new Date()
  }

  return false
}

/**
 * Gets the guild access start date if available.
 *
 * @param content - Content with watchableReasons or actionsToWatch array
 * @returns The start date for guild access, or null if not available
 */
export const getGuildAccessStartDate = (content?: ContentWithWatchableReasons | null): Date | null => {
  const reasons = getAvailabilityReasons(content)
  const guildReason = reasons?.find((reason) => reason?.__typename === 'GuildAvailabilityAction')

  if (guildReason?.startDatetime) {
    return new Date(guildReason.startDatetime)
  }

  return null
}

/**
 * Checks if content is currently streaming (either free or guild access).
 * Returns true if content is available via PubliclyAvailable OR GuildAvailabilityAction.
 *
 * For ContentSeries, pass the firstWatchable content.
 *
 * @param content - Content with watchableReasons array
 */
export const isStreaming = (content?: ContentWithWatchableReasons | null): boolean => {
  return isInFreeAccess(content) || isInGuildAccess(content)
}

/**
 * Checks if tickets are currently being sold for a theatrical release.
 * Returns true if the ticketStatus is 'PRE_SALES' or 'LIVE'.
 * @param ticketStatus - The ticket status from the theatrical release
 */
export const isSellingTickets = (ticketStatus?: TicketsStatusReference | null): boolean => {
  return ticketStatus === 'PRE_SALES' || ticketStatus === 'LIVE'
}

/**
 * Checks if tickets are currently being sold for a theatrical release in the future.
 * Returns true if the ticketStatus is 'PRE_SALES' or 'COMING_SOON'.
 * @param ticketStatus - The ticket status from the theatrical release
 */
export const willBeInTheaters = (ticketStatus?: TicketsStatusReference | null): boolean => {
  return ticketStatus === 'PRE_SALES' || ticketStatus === 'COMING_SOON'
}

/**
 * Checks if a project is currently in theaters.
 * Returns true if the ticketStatus is 'LIVE'.
 * @param ticketStatus - The ticket status from the theatrical release
 */
export const inTheaters = (ticketStatus?: TicketsStatusReference | null): boolean => {
  return ticketStatus === 'LIVE'
}

/**
 * Phase slug values used for analytics tracking.
 */
export type PhaseSlugForTracking = 'free_access' | 'guild_access' | 'pre_sale' | 'theatrical' | 'unknown'

/**
 * Maps content availability and ticket status to a phase slug for analytics tracking.
 * This replaces the old approach of deriving the phase from primaryFlowPhases.
 *
 * @param content - Content with watchableReasons array
 * @param ticketStatus - The ticket status from the theatrical release (optional)
 * @returns The phase slug string for analytics tracking
 */
export const getCurrentPhaseSlug = (
  content?: ContentWithWatchableReasons | null,
  ticketStatus?: TicketsStatusReference | null,
): PhaseSlugForTracking => {
  // Check streaming availability first
  if (isInFreeAccess(content)) {
    return 'free_access'
  }
  if (isInGuildAccess(content)) {
    return 'guild_access'
  }

  // Check theatrical status
  if (ticketStatus === 'LIVE') {
    return 'theatrical'
  }
  if (ticketStatus === 'PRE_SALES') {
    return 'pre_sale'
  }

  return 'unknown'
}
